import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import {
    CONFIG_TYPE, ZANCASORT_LOCATION_TYPES, ZANCASORT_LOCATION_SUB_TYPES,
    LOCATION_TYPE_SEPARATOR, ZANCASORT_LEGACY_LOCATION_TYPES
} from "../../../util/Constants";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import Select, {SelectOption} from "@amzn/meridian/select";
import {getLocalizedMessage} from "../../../util/Localization";
import {isValidZancaSortLocationId, parseLocationType, buildLocationType, validateZancaSortLocationConfig} from "../../../util/Validations";

const AddZancaSortLocationConfigurationModal = (props) => {
    const {formatMessage} = props.intl;
    const {mac, intl} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext)

    // Visual controls
    const {open, setOpen} = props
    const [show, setShow] = useState(false)
    const [edit, setEdit] = useState(false)

    // Values
    const [locationId, setLocationId] = useState("")
    const [locationType, setLocationType] = useState("")
    const [subType, setSubType] = useState("")

    // Error
    const [locationIdErr, setLocationIdErr] = useState(false)
    const [locationTypeErr, setLocationTypeErr] = useState(false)
    const [subTypeErr, setSubTypeErr] = useState(false)

    const onCloseModal = () => {
        setShow(false);
        setOpen(false)
        resetInputState();
    }

    const modalLocationConfig = {
        locationId: locationId,
        locationType: buildLocationType(locationType, locationType === ZANCASORT_LOCATION_TYPES.TETRIS_STATION.value ? subType : "")
    }

    /* Filling a map: locationConfigMap keyed by locationId (with the stored value parsed
       into type/subType) so an existing config can be prefilled when editing. */
    const locationConfigMap = {}
    getDraftData({stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION, subKey: mac}).forEach(value => {
        locationConfigMap[value.locationId] = parseLocationType(value.locationType);
    });

    const checkValidations = () => {
        return validateZancaSortLocationConfig(modalLocationConfig);
    }

    const onClickConfirm = (modalLocationConfig, mac) => {
        if (checkValidations()) {
            setConfigData(modalLocationConfig, mac);
            setOpen(false)
            resetInputState();
            setShow(false);
        } else {
            setShow(true)
        }
    }

    function resetInputState() {
        setLocationId("");
        setLocationIdErr(false);
        setLocationType("");
        setLocationTypeErr(false);
        setSubType("");
        setSubTypeErr(false);
        setEdit(false);
    }

    /* Maps a stored type to the modal dropdown value. Legacy STOW_LOCATION rows
       are edited as Tetris Station; any other unknown type (e.g. PICK_LOCATION) forces
       the user to pick a valid type. */
    const toDropdownLocationType = (type) => {
        if (Object.keys(ZANCASORT_LOCATION_TYPES).includes(type)) return type;
        if (type === ZANCASORT_LEGACY_LOCATION_TYPES.STOW_LOCATION) return ZANCASORT_LOCATION_TYPES.TETRIS_STATION.value;
        return "";
    }

    /* For legacy rows without a structured sub type, fall back to a trailing #A / #B
       convention inside the location id (display/prefill aid only). */
    const deriveLegacySubType = (id) => {
        const lastSegment = id.split(LOCATION_TYPE_SEPARATOR).pop();
        return Object.keys(ZANCASORT_LOCATION_SUB_TYPES).includes(lastSegment) ? lastSegment : "";
    }

    const prefill = () => {
        if (locationConfigMap.hasOwnProperty(locationId)) {
            const {type, subType: storedSubType} = locationConfigMap[locationId];
            const prefilledType = toDropdownLocationType(type);
            setEdit(true);
            setLocationType(prefilledType);
            setLocationTypeErr(false);
            setSubType(storedSubType || (type === ZANCASORT_LEGACY_LOCATION_TYPES.STOW_LOCATION ? deriveLegacySubType(locationId) : ""));
            setSubTypeErr(false);
            setLocationIdErr(!isValidZancaSortLocationId(locationId));
        } else if (edit) {
            setEdit(false);
            setLocationType("");
            setSubType("");
            setSubTypeErr(false);
        }
    }

    const onLocationTypeChange = (value) => {
        setLocationType(value);
        setLocationTypeErr(false);
        if (locationId.trim() !== "") {
            setLocationIdErr(!isValidZancaSortLocationId(locationId));
        }
        if (value !== ZANCASORT_LOCATION_TYPES.TETRIS_STATION.value) {
            setSubType("");
            setSubTypeErr(false);
        }
    }

    const setConfigData = (modalLocationConfig, mac) => {
        if(edit) {
            editInstance({
                stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
                subKey: mac,
                instance: modalLocationConfig,
                keyComparator: (config1, config2) => (
                    config1.locationId === config2.locationId
                ),
                valueComparator: (config1, config2) => (
                    config1.locationType === config2.locationType
                )
            })
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.ZANCA_SORT_LOCATION_CONFIGURATION,
                subKey: mac,
                instance: modalLocationConfig
            })
        }
    }

    return (
        <Row>
            <Modal
                title={formatMessage({id: "locationconfig.addupdate"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b> <FormattedMessage id="modal.addlocationheading"
                                              defaultMessage="To edit an existing configuration, enter the Location Id."/>
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="location-type-input">
                            {getLocalizedMessage({intl, stringId: "location.type"})}
                        </Text>
                        <Select id="location-type-input"
                            value={locationType}
                            onChange={onLocationTypeChange}
                            onBlur={() => {
                                setLocationTypeErr(locationType === "");
                            }}
                            placeholder={<FormattedMessage id="modal.enter_value"
                                               defaultMessage="Enter value..."/>}
                            width="100%"
                            error={locationTypeErr}>
                            {Object.values(ZANCASORT_LOCATION_TYPES).map(({label, value}, index) =>
                                <SelectOption key={index} value={value} label={label} />
                            )}
                        </Select>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="location-id-input">
                            {getLocalizedMessage({intl, stringId: "location.id"})}
                        </Text>
                        <Input id="location-id-input"
                               value={locationId}
                               onChange={setLocationId}
                               onBlur={() => {
                                   setLocationIdErr(locationId.trim() !== "" && !isValidZancaSortLocationId(locationId))
                                   prefill();
                               }}
                               error={locationIdErr}/>
                        {locationIdErr &&
                            <Text type="b100" color="error">
                                {getLocalizedMessage({intl, stringId: "location.invalidLocationId"})}
                            </Text>}
                    </Column>
                    {locationType === ZANCASORT_LOCATION_TYPES.TETRIS_STATION.value &&
                        <Column spacing="200">
                            <Text tag="label" type="b200" color="primary" htmlFor="sub-type-input">
                                {getLocalizedMessage({intl, stringId: "location.subType"})}
                            </Text>
                            <Select id="sub-type-input"
                                value={subType}
                                onChange={(value) => {
                                    setSubType(value);
                                    setSubTypeErr(false);
                                }}
                                onBlur={() => setSubTypeErr(subType === "")}
                                placeholder={<FormattedMessage id="modal.enter_value"
                                                   defaultMessage="Enter value..."/>}
                                width="100%"
                                error={subTypeErr}>
                                {Object.values(ZANCASORT_LOCATION_SUB_TYPES).map(({label, value}, index) =>
                                    <SelectOption key={index} value={value} label={label} />
                                )}
                            </Select>
                        </Column>}
                </Column>
                <ModalFooter>
                    <Column>
                        {!checkValidations() &&
                            show &&
                            <Alert type="warning"
                                   size="small">
                                <FormattedMessage id="alert.missingfields"
                                                  defaultMessage="Please enter all fields in correct format!"/>
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                        </Button>
                        <Button type="primary" size="medium"
                                onClick={() => onClickConfirm(modalLocationConfig, mac)}>
                            { edit ? <FormattedMessage id="modal.confirmUpdate" defaultMessage="Confirm update"/>
                                : <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(AddZancaSortLocationConfigurationModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/zancasort/modal/AddZancaSortLocationConfigurationModal.js